import { forwardRef, useEffect, useImperativeHandle, useRef } from "react";

const SCRIPT_ID = "cf-turnstile-script";
const SCRIPT_SRC = "https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit";

function loadTurnstileScript() {
  const existing = document.getElementById(SCRIPT_ID);
  if (existing) {
    return existing.dataset.loaded === "true"
      ? Promise.resolve()
      : new Promise((resolve, reject) => {
          existing.addEventListener("load", () => resolve(), { once: true });
          existing.addEventListener("error", () => reject(new Error("Failed to load Turnstile")), { once: true });
        });
  }

  return new Promise((resolve, reject) => {
    const script = document.createElement("script");
    script.id = SCRIPT_ID;
    script.src = SCRIPT_SRC;
    script.async = true;
    script.defer = true;
    script.addEventListener("load", () => {
      script.dataset.loaded = "true";
      resolve();
    }, { once: true });
    script.addEventListener("error", () => reject(new Error("Failed to load Turnstile")), { once: true });
    document.head.appendChild(script);
  });
}

const TurnstileWidget = forwardRef(function TurnstileWidget({ siteKey, action, resetKey = 0, onTokenChange }, ref) {
  const containerRef = useRef(null);
  const widgetIdRef = useRef(null);
  const onTokenChangeRef = useRef(onTokenChange);
  const previousResetKeyRef = useRef(resetKey);
  const pendingRef = useRef(null);
  const tokenRef = useRef({ token: "", at: 0 });

  useEffect(() => {
    onTokenChangeRef.current = onTokenChange;
  }, [onTokenChange]);

  const clearPending = (error) => {
    const pending = pendingRef.current;
    if (!pending) return;
    pendingRef.current = null;
    window.clearTimeout(pending.timer);
    if (error) pending.reject(error);
  };

  useEffect(() => {
    if (!siteKey || !containerRef.current) return undefined;

    let cancelled = false;

    const renderWidget = () => {
      if (cancelled || !window.turnstile || !containerRef.current) return;
      if (widgetIdRef.current != null) {
        window.turnstile.reset(widgetIdRef.current);
        return;
      }
      widgetIdRef.current = window.turnstile.render(containerRef.current, {
        sitekey: siteKey,
        action,
        callback: (token) => {
          tokenRef.current = { token, at: Date.now() };
          onTokenChangeRef.current?.(token);
          const pending = pendingRef.current;
          if (pending) {
            pendingRef.current = null;
            window.clearTimeout(pending.timer);
            pending.resolve(token);
          }
        },
        "expired-callback": () => {
          tokenRef.current = { token: "", at: 0 };
          onTokenChangeRef.current?.("");
          clearPending(new Error("Verification expired. Please try again."));
        },
        "error-callback": () => {
          tokenRef.current = { token: "", at: 0 };
          onTokenChangeRef.current?.("");
          clearPending(new Error("Verification failed. Please try again."));
        },
      });
    };

    loadTurnstileScript()
      .then(renderWidget)
      .catch(() => onTokenChangeRef.current?.(""));

    return () => {
      cancelled = true;
      clearPending(new Error("Verification was reset."));
      if (widgetIdRef.current != null && window.turnstile) {
        window.turnstile.remove(widgetIdRef.current);
        widgetIdRef.current = null;
      }
    };
  }, [siteKey, action]);

  useEffect(() => {
    if (previousResetKeyRef.current === resetKey) return;
    previousResetKeyRef.current = resetKey;
    tokenRef.current = { token: "", at: 0 };
    onTokenChangeRef.current?.("");
    if (widgetIdRef.current != null && window.turnstile) {
      window.turnstile.reset(widgetIdRef.current);
    }
  }, [resetKey]);

  useImperativeHandle(ref, () => ({
    getFreshToken() {
      const existing = tokenRef.current;
      if (existing.token && Date.now() - existing.at < 4 * 60 * 1000) {
        return Promise.resolve(existing.token);
      }
      return new Promise((resolve, reject) => {
        if (!window.turnstile || widgetIdRef.current == null) {
          reject(new Error("Please complete the verification check."));
          return;
        }
        clearPending(new Error("Verification was reset."));
        pendingRef.current = {
          resolve,
          reject,
          timer: window.setTimeout(() => {
            pendingRef.current = null;
            reject(new Error("Verification timed out. Please try again."));
          }, 10000),
        };
        window.turnstile.reset(widgetIdRef.current);
      });
    },
  }));

  if (!siteKey) return null;

  return <div ref={containerRef} className="cf-turnstile" data-action={action} />;
});

export default TurnstileWidget;
